import { useState } from 'react';
import {
  Modal, Button, Group, Stack, Text, Alert, SegmentedControl, Box, Badge,
} from '@mantine/core';
import { ShieldCheck, CheckCircle, Sun, Moon, Desktop, Lock } from '@phosphor-icons/react';
import { useMantineColorScheme } from '@mantine/core';
import { useAuthStore } from '../../store/auth.store';
import { AdminChangePasswordForm } from '../auth/AdminChangePasswordForm';
import { TotpSetupFlow } from '../auth/TotpSetupFlow';
import { TotpDisableForm } from '../auth/TotpDisableForm';
import { toast } from '../../utils/toast';

interface AdminSettingsModalProps {
  opened: boolean;
  onClose: () => void;
}

export function AdminSettingsModal({ opened, onClose }: AdminSettingsModalProps) {
  const { user, totpEnabled } = useAuthStore();
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [totpMode, setTotpMode] = useState<'idle' | 'setup' | 'disable'>('idle');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap={8}>
          <ShieldCheck size={18} weight="bold" color="var(--amber)" />
          <Text fw={600} size="sm" c="var(--text-primary)">Admin Account Settings</Text>
        </Group>
      }
      size="md"
      centered
    >
      <Stack gap={16} mt={8}>
        {/* Account Information */}
        <div style={{ background: 'var(--bg-input)', border: '1px solid var(--boundary)', borderRadius: 6, padding: 16 }}>
          <Text size="xs" fw={600} c="var(--text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Account Information
          </Text>
          <Group gap={8} mt={8}>
            <Text size="sm" fw={600} c="var(--text-primary)">{user?.full_name || 'Admin'}</Text>
            <span style={{
              background: 'var(--amber-dim)', border: '1px solid var(--amber)', color: 'var(--amber)',
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
            }}>
              PLATFORM ADMIN
            </span>
          </Group>
          <Text size="xs" c="var(--text-secondary)" mt={2}>{user?.email || 'admin@homehub.platform'}</Text>
        </div>

        {/* Appearance / Theme Settings */}
        <div style={{ background: 'var(--bg-input)', border: '1px solid var(--boundary)', borderRadius: 6, padding: 16 }}>
          <Text size="xs" fw={600} c="var(--text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }} mb={8}>
            Theme & Appearance
          </Text>
          <Box mt={8}>
            <SegmentedControl
              fullWidth
              value={colorScheme}
              onChange={(value) => setColorScheme(value as any)}
              data={[
                {
                  value: 'dark',
                  label: (
                    <Group gap={6} justify="center">
                      <Moon size={15} weight="duotone" />
                      <span>Dark</span>
                    </Group>
                  ),
                },
                {
                  value: 'light',
                  label: (
                    <Group gap={6} justify="center">
                      <Sun size={15} weight="duotone" />
                      <span>Light</span>
                    </Group>
                  ),
                },
                {
                  value: 'auto',
                  label: (
                    <Group gap={6} justify="center">
                      <Desktop size={15} weight="duotone" />
                      <span>Auto</span>
                    </Group>
                  ),
                },
              ]}
              styles={{
                root: { background: 'var(--surface)', border: '1px solid var(--boundary)', padding: 4, borderRadius: 6 },
                indicator: { background: 'var(--surface-hi)' },
                label: { color: 'var(--text-primary)', fontWeight: 500, fontSize: 12 },
              }}
            />
          </Box>
        </div>

        {/* Two-Factor Authentication (TOTP) */}
        <div style={{ background: 'var(--bg-input)', border: '1px solid var(--boundary)', borderRadius: 6, padding: 16 }}>
          <Group justify="space-between" mb={totpMode === 'idle' ? 0 : 12}>
            <div>
              <Group gap={8}>
                <Text size="xs" fw={600} c="var(--text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Two-Factor Authentication
                </Text>
                <Badge size="xs" color={totpEnabled ? 'teal' : 'gray'} variant="light">
                  {totpEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </Group>
              <Text size="xs" c="var(--text-secondary)" mt={4}>
                Require a code from an authenticator app at every sign-in.
              </Text>
            </div>
            {totpMode === 'idle' && (
              totpEnabled ? (
                <Button size="xs" variant="outline" color="red" onClick={() => setTotpMode('disable')}>
                  Disable
                </Button>
              ) : (
                <Button size="xs" color="amber" leftSection={<Lock size={12} />} onClick={() => setTotpMode('setup')}>
                  Set up
                </Button>
              )
            )}
          </Group>

          {totpMode === 'setup' && (
            <TotpSetupFlow
              onCancel={() => setTotpMode('idle')}
              onDone={() => {
                setTotpMode('idle');
                toast.success('Two-factor authentication is now enabled', '2FA Enabled');
              }}
            />
          )}
          {totpMode === 'disable' && (
            <TotpDisableForm
              onCancel={() => setTotpMode('idle')}
              onDisabled={() => {
                toast.info('Two-factor authentication disabled. Please sign in again.', '2FA Disabled');
                onClose();
              }}
            />
          )}
        </div>

        {/* Change Password Form */}
        <Stack gap={14}>
          <Text size="xs" fw={600} c="var(--text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Change Admin Password
          </Text>

          {passwordSuccess && (
            <Alert icon={<CheckCircle size={16} />} color="teal" styles={{ root: { background: 'var(--green-dim)', borderColor: 'var(--green-border)', color: 'var(--green)' } }}>
              Password changed successfully. Other devices have been signed out.
            </Alert>
          )}

          <AdminChangePasswordForm
            onSuccess={() => setPasswordSuccess(true)}
            onCancel={onClose}
          />
        </Stack>
      </Stack>
    </Modal>
  );
}
